<template>
  <b-card>
    <b-form ref="form" @submit.stop.prevent="handleSubmit">
      <b-form-group v-if="can.create && canUpdate">
        <b-form-input
          required
          name="title"
          v-model="title"
          :state="titleValidation"
          :validated="true"
          placeholder="Title"
          class="form-control"
          invalid-feedback="Title is required"
        ></b-form-input>
        <span class="text-danger">*</span> <span class="text-muted">Required</span>
      </b-form-group>

      <b-form-group v-if="canUpdate">
        <b-form-textarea
          id="textarea"
          name="description"
          v-if="can.create"
          v-model="description"
          placeholder="Description..."
          class="form-control"
          rows="3"
          max-rows="6"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Template">
        <b-form-radio-group
          v-model="template"
          :options="templateOptions"
          value-field="id"
          html-field="name"
        ></b-form-radio-group>
      </b-form-group>

      <b-form-group label="Display options">
        <b-form-radio-group
          v-model="display"
          :options="displayOptions"
          value-field="id"
          text-field="name"
          size="sm"
          buttons
        ></b-form-radio-group>
      </b-form-group>

      <b-form-group label="Subtask type">
        <b-form-radio-group
          v-model="counterType"
          :options="subtaskCounterOptions"
          value-field="id"
          text-field="name"
          size="sm"
          buttons
        ></b-form-radio-group>
      </b-form-group>

      <b-form-group label="Reveal image options" v-if="isImageReveal">
        <b-button @click="handleRevealImageModal" class="mb-3" size="sm">
          <b-icon icon="image"></b-icon> Manage images
        </b-button>

        <b-form-checkbox v-model="subtaskVisible" switch>Patient can see subtask count</b-form-checkbox>
      </b-form-group>

      <div v-if="showCardOrder">
        <h3>Card order</h3>
        <b-list-group class="mb-3">
          <draggable v-model="cardAttributes" @end="updateCardPositions" ghost-class="bg-light">
            <transition-group>
              <b-list-group-item
                class="pointer list-group-item pointer d-flex justify-content-between align-items-center"
                v-for="(card, index) in cardAttributes"
                :key="`selected_card${index}`"
              >
                <b-icon icon="arrow-down-up" aria-label="Draggable"></b-icon> {{ card.title }}
                <b-button class="text-right" variant="danger" size="sm" @click="removeCard(card)">
                  <b-icon icon="x" />
                </b-button>
              </b-list-group-item>
            </transition-group>
          </draggable>
        </b-list-group>
      </div>
    </b-form>
    <b-modal v-model="manageRevealImagesModal" title="Manage images used in image reveal" modal-tall size="lg">
      <RevealImageManager />
      <template #modal-footer>
        <div class="w-100">
          <b-button variant="primary" class="float-right" @click="handleRevealImageModal">Finished</b-button>
        </div>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { EventBus } from '../event-bus';
import RevealImageManager from './RevealImageManager.vue';

export default {
  data() {
    return {
      formSubmitted: false,
      manageRevealImagesModal: false,
    };
  },

  components: { draggable, RevealImageManager },

  mounted() {
    EventBus.$on('save-schedule', this.checkSubmit);
    EventBus.$on('update-schedule', this.checkUpdate);
    EventBus.$on('reset-form', this.resetForm);
  },

  destroyed() {
    EventBus.$off('save-schedule', this.checkSubmit);
    EventBus.$off('update-schedule', this.checkUpdate);
    EventBus.$off('reset-form', this.resetForm);
  },

  computed: {
    ...mapState(['can', 'visualSchedule', 'userId', 'subtaskCounterOptions', 'templateOptions', 'displayOptions']),
    ...mapGetters(['scheduleCards']),

    isImageReveal() {
      const subtask = this.subtaskCounterOptions.find((st) => st.id === this.visualSchedule.visual_schedule_subtask_id);
      if (subtask) return subtask.name.includes('Image');
      return false;
    },

    /**
     * Without the `?` safe operator, `/visual-schedules/:id` was throwing an error even though the state had the `can`
     * attribute.
     */
    canUpdate() {
      return this.visualSchedule.can?.update;
    },

    title: {
      get() {
        return this.visualSchedule.title;
      },
      set(val) {
        this.updateVisualSchedule({ field: 'title', value: val });
      },
    },

    description: {
      get() {
        return this.visualSchedule.description;
      },
      set(val) {
        this.updateVisualSchedule({ field: 'description', value: val });
      },
    },

    template: {
      get() {
        return this.visualSchedule.visual_schedule_template_id;
      },
      set(val) {
        this.updateVisualSchedule({ field: 'visual_schedule_template_id', value: val });
      },
    },

    display: {
      get() {
        return this.visualSchedule.visual_schedule_display_id;
      },
      set(val, prev) {
        if (this.can.changeDisplay) {
          this.updateVisualSchedule({ field: 'visual_schedule_display_id', value: val });
        } else if (prev) this.showUpgradeMessage('change the display settings');
      },
    },

    counterType: {
      get() {
        return this.visualSchedule.visual_schedule_subtask_id;
      },
      set(val) {
        this.updateVisualSchedule({ field: 'visual_schedule_subtask_id', value: val });
      },
    },

    subtaskVisible: {
      get() {
        return this.visualSchedule.patient_can_see_subtask_count;
      },
      set(val) {
        this.updateVisualSchedule({ field: 'patient_can_see_subtask_count', value: val });
      },
    },

    cardAttributes: {
      get() {
        return this.scheduleCards;
      },
      set(val) {
        this.updateVisualSchedule({ field: 'visual_schedule_cards_attributes', value: val });
      },
    },

    showCardOrder() {
      if (this.visualSchedule.id) {
        // we're editing an existing
        return this.can.manipulateCards;
      }
      return this.cardAttributes.length;
    },

    titleValidation() {
      if (!this.formSubmitted) {
        return null;
      }
      return this.visualSchedule.title.length !== 0;
    },
  },

  methods: {
    ...mapMutations(['updateVisualSchedule', 'removeCard']),
    ...mapActions(['updateCardPositions', 'setVisualSchedule']),

    checkSubmit() {
      this.formSubmitted = true;
      if (this.titleValidation) this.handleSubmit();
    },

    checkUpdate() {
      // If the `current_user` is viewing someone else's VisualSchedule, they'll not have the permission to update it.
      // In that case, we just want to update the Vuex state with their changes and show a success message.
      if (!this.canUpdate) {
        this.formSubmitted = false;
        this.$toast.open('Changes saved!');
        return;
      }

      this.formSubmitted = true;
      if (this.titleValidation) this.handleUpdate();
    },

    handleUpdate() {
      axios
        .patch(`/api/v2/visual-schedules/${this.visualSchedule.id}`, {
          visual_schedule: this.visualSchedule,
        })
        .then((response) => {
          if (response.status === 200) {
            this.$toast.open('Changes saved!');
            this.formSubmitted = false;
            // Update state with latest relationship data like cards, etc.
            this.setVisualSchedule(this.visualSchedule.id);
          } else {
            this.$toast.open({
              type: 'error',
              message: 'Something went wrong. Try again',
            });
          }
        });
    },

    handleSubmit() {
      axios
        .post('/api/v2/visual-schedules', {
          visual_schedule: this.visualSchedule,
        })
        .then((response) => {
          if (response.status === 201) {
            window.location.href = `/visual-schedules/${response.data.success.id}`;
            this.formSubmitted = false;
          } else {
            this.$toast.open({
              type: 'error',
              message: 'Something went wrong. Try again',
            });
          }
        });
    },

    handlePosition() {
      this.visualSchedule.visual_schedule_cards_attributes = this.visualSchedule.visual_schedule_cards_attributes.map(
        (card, index) => ({ title: card.title, card_id: card.card_id, position: index + 1 })
      );
    },

    resetForm() {
      this.formSubmitted = false;
      this.template = this.templateOptions[0].id;
      this.display = this.displayOptions[0].id;
      this.counterType = this.subtaskCounterOptions[0].id;
    },

    handleRevealImageModal() {
      if (this.can.customRevealImg) {
        this.manageRevealImagesModal = !this.manageRevealImagesModal;
      } else this.showUpgradeMessage('choose custom reveal images');
    },

    showUpgradeMessage(message) {
      this.$toast.info(`You need to have an active Therapy Tools add-on to ${message}`, {
        duration: 10000,
        dismissible: true,
      });
    },
  },
};
</script>
